import Constants from 'expo-constants';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { segmentClient } from '@/util/analytics';

/**
 * Determines auth state string from user status flags.
 */
function getAuthState(
  userId: string | undefined,
  isPending: boolean,
  needsNpiVerification: boolean
): 'unauthenticated' | 'pending_oauth' | 'pending_npi_verification' | 'authenticated' {
  if (!userId) {
    return 'unauthenticated';
  }

  if (isPending) {
    return 'pending_oauth';
  }

  if (needsNpiVerification) {
    return 'pending_npi_verification';
  }

  return 'authenticated';
}

/**
 * Tracks app initialization event with auth state, user info, and performance metrics.
 * Auth states: 'unauthenticated', 'pending_oauth', 'pending_npi_verification', 'authenticated'.
 */
export function trackAppInitialized(properties: {
  userId: string | undefined;
  isPending: boolean;
  needsNpiVerification: boolean;
  user:
    | {
        userType?: string | null;
        firstName?: string | null;
        lastName?: string | null;
      }
    | null
    | undefined;
  hasUpdate: boolean;
  loadTimeMs: number;
}) {
  const { userId, isPending, needsNpiVerification, user, hasUpdate, loadTimeMs } = properties;

  const authState = getAuthState(userId, isPending, needsNpiVerification);

  segmentClient.track(ANALYTICS_EVENTS.APP_INITIALIZED, {
    authState,
    isAuthenticated: authState === 'authenticated',
    userId: userId || null,
    userType: user?.userType || null,
    userName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || null : null,
    hasUpdate,
    loadTimeMs,
    currentVersion: Constants.expoConfig?.version,
    buildNumber:
      Constants.expoConfig?.ios?.buildNumber || Constants.expoConfig?.android?.versionCode,
  });
}
